import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { AlertCircle, CheckSquare, Square } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { BASE_URL } from '../config';

export default function AddonCredits() {
  const [addonBalance, setAddonBalance] = useState(0);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [creditsToAssign, setCreditsToAssign] = useState('');
  const [loading, setLoading] = useState(false);
  const [hospitalId, setHospitalId] = useState(null);
  const [isMultiSelect, setIsMultiSelect] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const id = localStorage.getItem('hospitalId');
    setHospitalId(id);
    if (id) {
      fetchAddonBalance(id);
      fetchDoctors(id);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const fetchAddonBalance = async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`${BASE_URL}/api/superadmin/addon-balance/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAddonBalance(res.data.add_on);
    } catch (err) {
      console.error('Error fetching add-on balance:', err);
    }
  };

  const fetchDoctors = async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      // console.log('🔍 Fetching doctors for clinic ID:', id);
      const res = await axios.get(`${BASE_URL}/api/superadmin/${id}/doctors/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // console.log('📦 Doctors response:', res.data);
      // console.log('👥 Number of doctors:', res.data.doctors?.length || 0);
      setDoctors(res.data.doctors || []);
    } catch (err) {
      console.error('❌ Error fetching doctors:', err);
      console.error('Error details:', err.response?.data);
    }
  };

  const toggleDoctorSelection = (doctorId) => {
    setSelectedDoctors((prev) =>
      prev.includes(doctorId)
        ? prev.filter((id) => id !== doctorId)
        : [...prev, doctorId]
    );
  };

  const handleAssignCredits = async (e) => {
    e.preventDefault();
    
    // Validation for single select
    if (!isMultiSelect && !selectedDoctor) {
      toast.error('Please select a doctor');
      return;
    }

    // Validation for multi select
    if (isMultiSelect && selectedDoctors.length === 0) {
      toast.error('Please select at least one doctor');
      return;
    }

    if (!creditsToAssign || parseFloat(creditsToAssign) <= 0) {
      toast.error('Please enter valid credits');
      return;
    }

    // Calculate total credits needed
    const creditsPerDoctor = parseFloat(creditsToAssign);
    const doctorCount = isMultiSelect ? selectedDoctors.length : 1;
    const totalCreditsNeeded = creditsPerDoctor * doctorCount;

    // Check if total exceeds available add-on credits
    if (totalCreditsNeeded > addonBalance) {
      toast.error(`Credits exceeded! You need ${totalCreditsNeeded.toFixed(2)} credits but only have ${addonBalance.toFixed(2)} available.`);
      return;
    }

    const confirmMessage = isMultiSelect
      ? `Assign ${creditsPerDoctor} credits to each of ${doctorCount} selected doctors (Total: ${totalCreditsNeeded.toFixed(2)} credits)?`
      : `Assign ${creditsPerDoctor} credits to selected doctor?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      
      if (isMultiSelect) {
        // Multi-select: assign to multiple doctors
        const promises = selectedDoctors.map((doctorId) =>
          axios.post(
            `${BASE_URL}/api/superadmin/distribute-addon/`,
            {
              hospital_id: hospitalId,
              doctor_id: doctorId,
              credits: creditsToAssign,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        );

        await Promise.all(promises);
        toast.success(`Successfully assigned ${creditsPerDoctor} credits to ${doctorCount} doctors!`);
        setSelectedDoctors([]);
      } else {
        // Single select
        const res = await axios.post(
          `${BASE_URL}/api/superadmin/distribute-addon/`,
          {
            hospital_id: hospitalId,
            doctor_id: selectedDoctor,
            credits: creditsToAssign,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(res.data.message);
        setSelectedDoctor('');
      }

      // Refresh data
      await fetchAddonBalance(hospitalId);
      setCreditsToAssign('');
      fetchDoctors(hospitalId);
    } catch (err) {
      console.error('Error assigning credits:', err);
      toast.error(err.response?.data?.error || 'Error assigning credits');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <Toaster position="top-right" />
      
      {/* Page Header with Balance */}
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Add-On Credits</h2>
            <p className="text-sm text-slate-600 mt-1">Distribute additional credits to doctors</p>
          </div>
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg px-6 py-3 shadow-lg shadow-amber-500/30">
            <p className="text-xs text-amber-100 mb-1 font-medium">Available Balance</p>
            <p className="text-2xl font-bold text-slate-900">{addonBalance.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Assign Credits Form - Enhanced Design */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-xl">
        <div className="bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-4">
          <h3 className="text-lg font-semibold text-slate-900">Assign Credits to Doctors</h3>
          <p className="text-sm text-primary-100 mt-0.5">Distribute your add-on credits efficiently</p>
        </div>

        <div className="p-6">
          {addonBalance <= 0 ? (
            <div className="bg-gradient-to-r from-amber-500/10 to-amber-600/10 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3">
              <div className="bg-amber-500/20 rounded-full p-2">
                <AlertCircle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-amber-200 font-semibold">No Add-On Credits Available</p>
                <p className="text-sm text-amber-300/80 mt-1">Contact your administrator to purchase additional credits.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleAssignCredits} className="space-y-5">
              {/* Multi-Select Toggle - Enhanced */}
              <div className="flex items-center justify-between pb-4 border-b border-slate-200/50">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                  <span className="text-sm font-medium text-slate-700">Selection Mode</span>
                </div>
                <div className="flex items-center gap-3">
                  {isMultiSelect && selectedDoctors.length > 0 && (
                    <span className="text-sm text-primary-400 font-medium bg-primary-500/10 px-3 py-1 rounded-full">
                      {selectedDoctors.length} doctor{selectedDoctors.length > 1 ? 's' : ''} selected
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setIsMultiSelect(!isMultiSelect);
                      setSelectedDoctor('');
                      setSelectedDoctors([]);
                      setShowDropdown(false);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-md ${
                      isMultiSelect
                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-primary-500/30'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {isMultiSelect ? (
                      <CheckSquare className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    Multi-Select
                  </button>
                </div>
              </div>

              {/* Form Fields - Enhanced */}
              <div className="bg-slate-50 rounded-lg p-5 border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-slate-700 mb-2.5">
                      {isMultiSelect ? '👥 Select Doctors' : '👤 Select Doctor'}
                    </label>
                    
                    {isMultiSelect ? (
                      <div className="relative" ref={dropdownRef}>
                        <button
                          type="button"
                          onClick={() => setShowDropdown(!showDropdown)}
                          className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 hover:border-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 outline-none text-left text-sm transition-all shadow-sm"
                        >
                          {selectedDoctors.length === 0
                            ? '-- Select doctors --'
                            : `${selectedDoctors.length} doctor${selectedDoctors.length > 1 ? 's' : ''} selected`}
                        </button>
                        
                        {showDropdown && (
                          <div className="absolute z-50 w-full mt-2 bg-white border border-slate-300 rounded-lg shadow-2xl max-h-64 overflow-y-auto">
                            {doctors.map((doc) => (
                              <div
                                key={doc.user_ID}
                                onClick={() => toggleDoctorSelection(doc.user_ID)}
                                className="flex items-start gap-3 px-4 py-3 hover:bg-slate-200 cursor-pointer border-b border-slate-600 last:border-b-0 transition-colors"
                              >
                                <div className="mt-0.5">
                                  {selectedDoctors.includes(doc.user_ID) ? (
                                    <CheckSquare className="w-5 h-5 text-primary-400" />
                                  ) : (
                                    <Square className="w-5 h-5 text-slate-600" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-slate-900">{doc.name}</p>
                                  <p className="text-xs text-slate-600 mt-0.5">{doc.email}</p>
                                  <p className="text-xs text-primary-400 mt-1">Credits: {doc.credits_remaining}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <select
                        value={selectedDoctor}
                        onChange={(e) => setSelectedDoctor(e.target.value)}
                        required
                        disabled={addonBalance <= 0}
                        className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 hover:border-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 outline-none disabled:opacity-50 text-sm transition-all shadow-sm"
                      >
                        <option value="">-- Select a doctor --</option>
                        {doctors.map((doc) => (
                          <option key={doc.user_ID} value={doc.user_ID}>
                            {doc.name} - {doc.email} (Credits: {doc.credits_remaining})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="w-72">
                    <label className="block text-sm font-semibold text-slate-700 mb-2.5">
                      💳 Credits to Assign {isMultiSelect && <span className="text-primary-400">(per doctor)</span>}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={addonBalance}
                      value={creditsToAssign}
                      onChange={(e) => setCreditsToAssign(e.target.value)}
                      required
                      disabled={addonBalance <= 0}
                      placeholder="0.00"
                      className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 hover:border-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 outline-none disabled:opacity-50 text-sm transition-all shadow-sm"
                    />
                    {isMultiSelect && selectedDoctors.length > 0 && creditsToAssign && (
                      <div className="mt-2 bg-primary-50 border border-primary-300 rounded-lg px-3 py-2">
                        <p className="text-xs text-primary-700 font-medium">
                          Total: <span className="text-primary-600 font-bold">{(parseFloat(creditsToAssign) * selectedDoctors.length).toFixed(2)}</span> credits
                        </p>
                      </div>
                    )}
                    
                    {/* Confirm Button - Enhanced */}
                    <button
                      type="submit"
                      disabled={loading || addonBalance <= 0}
                      className="mt-4 w-full px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Assigning...
                        </span>
                      ) : (
                        '✓ Confirm Assignment'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Doctors List Table */}
      <div className="bg-white rounded border border-slate-200 p-5">
        <h3 className="text-base font-semibold text-slate-900 mb-4">
          Doctors ({doctors.length})
        </h3>
        {doctors.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-700 uppercase">Name</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-700 uppercase">Email</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-700 uppercase">Package</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-700 uppercase">Credits</th>
                </tr>
              </thead>
              <tbody>
                {doctors.map((doc) => (
                  <tr key={doc.user_ID} className="border-b border-slate-200 hover:bg-slate-100">
                    <td className="py-2 px-3 text-sm text-slate-900">{doc.name}</td>
                    <td className="py-2 px-3 text-sm text-slate-600">{doc.email}</td>
                    <td className="py-2 px-3 text-sm text-slate-600">{doc.package}</td>
                    <td className="py-2 px-3 text-sm text-right font-semibold text-primary-600">
                      {doc.credits_remaining !== null ? doc.credits_remaining.toFixed(2) : 'Unlimited'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-slate-600 py-6 text-sm">No doctors found</p>
        )}
      </div>
    </div>
  );
}
